import React, { useState } from 'react';
import { Paperclip, DownloadSimple, ArrowsOutSimple } from '@phosphor-icons/react';

// Extract every <img src="..."> from an HTML blob.
function extractImages(html) {
  if (!html || typeof html !== 'string') return [];
  const out = [];
  const re = /<img[^>]+src="([^"]+)"(?:[^>]*alt="([^"]*)")?[^>]*>/gi;
  let m;
  while ((m = re.exec(html)) !== null) {
    out.push({ url: m[1], alt: m[2] || '' });
  }
  return out;
}

// Prettify an uploaded-file URL into a short filename (e.g. "IMG_2031.png").
function fileNameFromUrl(url) {
  try {
    const clean = url.split('?')[0];
    const name = clean.substring(clean.lastIndexOf('/') + 1);
    return decodeURIComponent(name) || 'attachment';
  } catch { return 'attachment'; }
}

// Jira-style "Attachments" section showing thumbnails of every image embedded
// in the task description. We intentionally DO NOT include comment images —
// keeps the list focused on primary task artifacts.
export default function DescriptionAttachments({ html }) {
  const images = extractImages(html);
  const [lightbox, setLightbox] = useState(null); // { url, alt }
  if (!images.length) return null;

  return (
    <div className="mt-4 border-t border-zinc-100 pt-4" data-testid="description-attachments">
      <div className="flex items-center gap-2 mb-2">
        <Paperclip size={13} className="text-zinc-400" />
        <h4 className="text-[11px] uppercase tracking-[0.12em] text-zinc-600 font-bold">Attachments</h4>
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded bg-zinc-100 text-zinc-600 text-[10px] font-bold" data-testid="description-attachments-count">{images.length}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) => (
          <div key={i} className="group relative w-[120px] rounded-lg overflow-hidden border border-zinc-200 hover:border-blue-400 bg-white shadow-sm hover:shadow-md transition-all" data-testid={`description-attachment-${i}`}>
            <button type="button" onClick={() => setLightbox(img)}
              className="block w-full h-[80px] bg-zinc-50 flex items-center justify-center overflow-hidden">
              <img src={img.url} alt={img.alt || ''} className="max-w-full max-h-full object-contain" loading="lazy" />
            </button>
            <div className="px-1.5 py-1 bg-white flex items-center gap-1">
              <span className="flex-1 text-[10px] text-zinc-600 truncate font-medium" title={fileNameFromUrl(img.url)}>{fileNameFromUrl(img.url)}</span>
              <a href={img.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                title="Open original"
                className="h-5 w-5 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-400 hover:text-blue-600 transition-colors">
                <DownloadSimple size={11} weight="bold" />
              </a>
            </div>
            <button type="button" onClick={() => setLightbox(img)} title="Preview"
              className="absolute top-1 right-1 h-6 w-6 rounded-md bg-black/55 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center backdrop-blur-sm transition-opacity">
              <ArrowsOutSimple size={12} weight="bold" />
            </button>
          </div>
        ))}
      </div>

      {/* Lightweight click-to-preview lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-8 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="attachment-lightbox">
          <img src={lightbox.url} alt={lightbox.alt || ''} className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}
